import { Injectable } from '@angular/core';
import { OfflinePhotoData, PendingPhotoUpload } from './models/offline-photo-data';
import { PhotoViewModel } from './models/photo-view.model';
import { PhotoService } from './photo.service';
import { InspectionItemPhotoUploadRequest } from './models/inspection-item-photo-upload-request.model';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { AssetOfflineService } from '../asset-offline.service';
import { OfflineService, Util } from '@phdsoft/common';
@Injectable({
  providedIn: 'root'
})
export class OfflinePhotoService {
  private readonly PHOTO_CONTENT_STORE = 'photo_content';
  private readonly PHOTO_METADATA_STORE = 'photo_metadata';
  private readonly PENDING_UPLOADS_STORE = 'pending_photo_uploads';

  constructor(
    private readonly photoService: PhotoService,
    private readonly offlineService: OfflineService, // general purpose offline storage service
    private readonly dbService: NgxIndexedDBService // used here for offline photo data
  ) { }

  // TODO: this method was replicated exactly as is from AssetOfflineService due to circular dependency issues. Extract to a shared service?
  async getAssetOfflineRegionIds(assetId: string, regionIds: string[]): Promise<string[]> {
    // Use Promise.all to wait for all checks to complete
    const results = await Promise.all(
      regionIds.map(async (regionId) => {
        const offlineId = `${assetId}_##_${regionId}`;
        const offlineRegionData = await this.offlineService.getItem(offlineId);

        // Return either the id or null
        return !Util.isNullOrUndefined(offlineRegionData) ? regionId : null;
      })
    );

    // Filter out null values
    return results.filter((id): id is string => id !== null);
  }


  async clearOfflinePhotos(assetId: string, regionId: string) {
    let photos = await lastValueFrom(this.dbService.getAll<OfflinePhotoData>(this.PHOTO_METADATA_STORE));
    photos = photos.filter(p => p.assetId === assetId && p.regionIds.includes(regionId));
    for (const photo of photos) {
      if (photo.regionIds.length == 1) { // remove completely
        await lastValueFrom(this.dbService.deleteByKey(this.PHOTO_METADATA_STORE, photo.id));
        await lastValueFrom(this.dbService.deleteByKey(this.PHOTO_CONTENT_STORE, photo.id));
      }
      else { // just "remove" from region
        photo.regionIds = photo.regionIds.filter(id => id !== regionId);
        await lastValueFrom(this.dbService.update(this.PHOTO_METADATA_STORE, photo));
      }
    }
  }

  async saveInspectionPhotosToOffline(assetId: string, regionId: string) {
    try {
      let photos: PhotoViewModel[] = []; // TODO: fetch from backend
      for (const photo of photos) {
        await this.persistOfflinePhoto(photo, assetId, regionId);
      }
    } catch (error) {
      console.error('Error saving photo offline:', error);
      throw error;
    }
  }

  async persistOfflinePhoto(photo: PhotoViewModel, assetId: string, regionId: string) {
    // check if photo is already in offline storage and update regionIds if needed
    let storedPhoto = await lastValueFrom(this.dbService.getByID<OfflinePhotoData>(this.PHOTO_METADATA_STORE, photo.Id));
    if (storedPhoto) {
      if (!storedPhoto.regionIds.includes(regionId)) {
        storedPhoto.regionIds.push(regionId);
        await lastValueFrom(this.dbService.update(this.PHOTO_METADATA_STORE, storedPhoto));
      }
      return;
    }

    if (photo.SecureUrl) {
      // Store binary content directly
      const response = await fetch(photo.SecureUrl);
      const content = await response.arrayBuffer();
      await lastValueFrom(this.dbService.add(this.PHOTO_CONTENT_STORE, content, photo.Id));

      let offlinePhotoData: OfflinePhotoData = {
        photo: photo,

        id: photo.Id,
        assetId: assetId,
        regionIds: [regionId]
      }

      // Store metadata separately
      await lastValueFrom(this.dbService.add(this.PHOTO_METADATA_STORE, offlinePhotoData));
    }
  }

  async persistPendingPhotoUpload(photoUpload: PendingPhotoUpload, file: File) {
    try {
      // Store the file content
      await lastValueFrom(this.dbService.add(this.PHOTO_CONTENT_STORE, await file.arrayBuffer(), photoUpload.tmpId));

      // Store metadata
      await lastValueFrom(this.dbService.add(this.PENDING_UPLOADS_STORE, photoUpload));
    } catch (error) {
      console.error('Error saving pending photo:', error);
      throw error;
    }
  }

  async getPhotoContent(photoId: string): Promise<ArrayBuffer | null> {
    const result = await firstValueFrom(this.dbService.getByID(this.PHOTO_CONTENT_STORE, photoId));
    return result as ArrayBuffer | null;
  }

  async getOfflinePhotos(assetId: string, regionId: string): Promise<OfflinePhotoData[]> {
    const photos = await lastValueFrom(this.dbService.getAllByIndex<OfflinePhotoData>(
      this.PHOTO_METADATA_STORE,
      'assetId',
      IDBKeyRange.only(assetId)  // Create an exact match key range
    ));
    return photos.filter(p => p.regionIds.includes(regionId));
  }

  // TODO: filter assets and regions
  async getPendingPhotoUploads(): Promise<PendingPhotoUpload[]> {
    return await lastValueFrom(this.dbService.getAll<PendingPhotoUpload>(this.PENDING_UPLOADS_STORE));
  }

  async uploadPendingPhotos() {
    const pendingUploads = await this.getPendingPhotoUploads();
    for (const pendingUpload of pendingUploads) {
      const content = await this.getPhotoContent(pendingUpload.tmpId);
      let file = new File([content], pendingUpload.fileMetadata.name, { type: pendingUpload.fileMetadata.type });

      switch (pendingUpload.uploadRequestType) {
        case 'InspectionItemPhotoUploadRequest':
          this.photoService.addInspectionItemPhoto(pendingUpload.uploadRequest as InspectionItemPhotoUploadRequest, file).subscribe({
            next: async (photo) => {
              // delete pending upload and photo content (with tmp id)
              await lastValueFrom(this.dbService.deleteByKey(this.PENDING_UPLOADS_STORE, pendingUpload.tmpId));
              await lastValueFrom(this.dbService.deleteByKey(this.PHOTO_CONTENT_STORE, pendingUpload.tmpId));


              let offlineRegionIds = await this.getAssetOfflineRegionIds(pendingUpload.assetId, pendingUpload.regionIds);
              if (offlineRegionIds.length === 0) {
                return;
              }

              // store metadata and photo content (with correct id)
              let offlinePhotoData: OfflinePhotoData = {
                photo: photo,

                id: photo.Id,
                assetId: pendingUpload.assetId,
                regionIds: offlineRegionIds // Only for offline regions!
              }
              await lastValueFrom(this.dbService.add(this.PHOTO_CONTENT_STORE, content, photo.Id));
              await lastValueFrom(this.dbService.add(this.PHOTO_METADATA_STORE, offlinePhotoData));
              console.log('Photo uploaded:', photo);
            },
            error: (error) => {
              console.error('Error uploading photo:', error);
            }
          });
          break;
        default:
          break;
      }
    }
  }

  async deleteOfflinePhoto(photoId: string): Promise<void> {
    await lastValueFrom(this.dbService.deleteByKey(this.PHOTO_METADATA_STORE, photoId));
    await lastValueFrom(this.dbService.deleteByKey(this.PHOTO_CONTENT_STORE, photoId));
    await lastValueFrom(this.dbService.deleteByKey(this.PENDING_UPLOADS_STORE, photoId));

    this.photoService.deletePhoto(photoId).subscribe({
      next: () => {
        console.log('Photo deleted:', photoId);
      },
      error: (error) => {
        console.error('Error deleting photo:', error);
      }
    });
  }
}
